import { Fragment } from "react";
import type {
  CSSProperties,
  InputHTMLAttributes,
  KeyboardEvent,
  MouseEventHandler,
  ReactNode,
  Ref
} from "react";
import style from "./style.module.css";
import { isNil, noop } from "lib/lodash";
import keyCodes from "lib/keyCodes";
import CrossThinIcon from "views/components/icons/CrossThinIcon";

const defaultOnKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
  /* We stop propagation to prevent elements to be deleted from canvas */
  if (keyCodes.isBlackListed(event.keyCode)) {
    event.stopPropagation();
  }
};

// The rest props are spread onto the <input>, so every native input
// attribute is accepted alongside the wrapper props below.
interface InputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "style"
> {
  /** Shown above the field. */
  label?: ReactNode;
  labelClassName?: string;
  /** Applied to the wrapping div, not the input. */
  className?: string;
  inputClassName?: string;
  /** Renders a clear cross which calls this when clicked. */
  clearInput?: MouseEventHandler<HTMLDivElement>;
  crossThinIconColor?: string;
  /** Suppresses the clear cross even when clearInput is given. */
  isEmptySearch?: boolean;
  error?: ReactNode;
  showErrorMsg?: boolean;
  onInputClick?: MouseEventHandler<HTMLInputElement>;
  ref?: Ref<HTMLInputElement>;
  /** Applied to the wrapping div. */
  style?: CSSProperties;
}

const Input = ({
  className,
  clearInput,
  crossThinIconColor = "white",
  error,
  inputClassName,
  isEmptySearch,
  label,
  labelClassName,
  onBlur = noop,
  onFocus,
  onInputClick = noop,
  onKeyDown = defaultOnKeyDown,
  onKeyPress,
  ref,
  showErrorMsg,
  style: propsStyle,
  value,
  ...props
}: InputProps) => (
  <Fragment>
    {label && <div className={`${style.label} ${labelClassName}`}>{label}</div>}
    <div
      className={`${style.div} ${className}`}
      data-error={!isNil(error)}
      data-disabled={props.disabled}
      data-testid="Input"
      style={propsStyle}
    >
      <input
        className={inputClassName}
        value={value}
        onKeyPress={onKeyPress}
        onKeyDown={onKeyDown}
        onFocus={onFocus}
        onBlur={onBlur}
        onClick={onInputClick}
        ref={ref}
        {...props}
      />
      {Boolean(clearInput) && !isEmptySearch && (
        <div
          className={style.clearCross}
          onClick={clearInput}
          data-testid="CrossThinIcon"
        >
          <CrossThinIcon size="11" color={crossThinIconColor} />
        </div>
      )}
      {showErrorMsg && error && <span className={style.error}>{error}</span>}
    </div>
  </Fragment>
);

export default Input;
